import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { makeStyles } from '@material-ui/core/styles'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import AssessmentIcon from '@material-ui/icons/Assessment'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import HistoryIcon from '@material-ui/icons/History'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import MyLocationIcon from '@material-ui/icons/MyLocation'
import RefreshIcon from '@material-ui/icons/Refresh'
import ScheduleIcon from '@material-ui/icons/Schedule'

import {
  filterQueueStops,
  formatActivityAction,
  formatAge,
  formatDistance,
  formatPercent,
  formatScore
} from './siteAuditDashboardUtils'

const LOCATIONS = [
  {
    key: 'hmi-yard',
    label: 'HMI Yard',
    latitude: 33.43730763333812,
    longitude: -112.11879940396678
  },
  {
    key: 'phoenix-center',
    label: 'Phoenix Center',
    latitude: 33.4484,
    longitude: -112.074
  }
]

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    padding: theme.spacing(2.5),
    background: theme.palette.background.default
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  locationField: {
    minWidth: 180
  },
  statCard: {
    height: '100%',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1]
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.1
  },
  workspace: {
    marginTop: theme.spacing(2)
  },
  queueCard: {
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    flexWrap: 'wrap'
  },
  searchField: {
    minWidth: 240
  },
  table: {
    minWidth: 880
  },
  tableHeader: {
    background: theme.palette.background.default
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.action.hover
    }
  },
  selectedRow: {
    background: `${theme.palette.primary.light} !important`
  },
  rank: {
    width: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    fontWeight: 700,
    color: theme.palette.primary.dark,
    background: theme.palette.primary.light
  },
  propertyName: {
    fontWeight: 700
  },
  detailCard: {
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(110px, 1fr))',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(110px, 1fr))'
    }
  },
  detailMetric: {
    padding: theme.spacing(1.25),
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`
  },
  activityCard: {
    height: '100%',
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden'
  },
  activityList: {
    maxHeight: 720,
    overflowY: 'auto'
  },
  activityItem: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  activityIcon: {
    width: 30,
    height: 30,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    marginRight: theme.spacing(1.25),
    flexShrink: 0
  },
  empty: {
    padding: theme.spacing(6, 2),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  loading: {
    minHeight: 240,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}))

const formatDateTime = value => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

const StatCard = ({ label, value, helper, color }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="overline">{label}</Typography>
      <Typography style={{ fontSize: 24, fontWeight: 700, color }}>{value}</Typography>
      <Typography variant="caption">{helper}</Typography>
    </CardContent>
  </Card>
)

const SiteAuditQueueDashboard = ({ url }) => {
  const classes = useStyles()
  const [location, setLocation] = useState(LOCATIONS[0])
  const [queue, setQueue] = useState(null)
  const [activity, setActivity] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedPropertyId, setSelectedPropertyId] = useState(null)
  const [notice, setNotice] = useState(null)

  const loadDashboard = useCallback(async ({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true)
    else setLoading(true)
    try {
      const queueResponse = await axios.get(`${url}/api/mobileapp/site-audits/queue`, {
        params: {
          latitude: location.latitude,
          longitude: location.longitude,
          limit: 10
        }
      })
      const queueData = queueResponse.data || {}
      setQueue(queueData)
      setSelectedPropertyId(current => {
        if ((queueData.stops || []).some(stop => stop.aspirePropertyId === current)) return current
        return queueData.nextStop?.aspirePropertyId || queueData.stops?.[0]?.aspirePropertyId || null
      })

      const activityResponse = await axios.get(`${url}/api/mobileapp/site-audits/activity`, {
        params: { limit: 50 }
      })
      setActivity(activityResponse.data?.activity || [])
    } catch (error) {
      setNotice({
        severity: 'error',
        message: error.response?.data?.error || error.message || 'Unable to load the Site Audit queue.'
      })
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [location.latitude, location.longitude, url])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setNotice({ severity: 'warning', message: 'Browser location is not available.' })
      return
    }
    navigator.geolocation.getCurrentPosition(
      position => {
        setLocation({
          key: 'current-location',
          label: 'Current Location',
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        })
      },
      error => setNotice({ severity: 'warning', message: error.message || 'Could not read current location.' }),
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  const handleAspireSync = async () => {
    setSyncing(true)
    try {
      const response = await axios.post(`${url}/api/mobileapp/site-audits/sync`)
      const result = response.data || {}
      setNotice({
        severity: 'success',
        message: `Aspire sync complete: ${result.contractedMatches || 0} contracted audits matched, ${result.upserted || 0} added, ${result.modified || 0} updated.`
      })
      await loadDashboard({ quiet: true })
    } catch (error) {
      setNotice({
        severity: 'error',
        message: error.response?.data?.error || error.message || 'Unable to sync Site Audits from Aspire.'
      })
    } finally {
      setSyncing(false)
    }
  }

  const filteredStops = useMemo(
    () => filterQueueStops(queue?.stops || [], search),
    [queue?.stops, search]
  )
  const selectedStop = useMemo(
    () => (queue?.stops || []).find(stop => stop.aspirePropertyId === selectedPropertyId) || null,
    [queue?.stops, selectedPropertyId]
  )
  const counts = queue?.counts || {}

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box>
          <Box className={classes.titleRow}>
            <AssessmentIcon color="primary" />
            <Typography variant="h1">Site Audit Queue</Typography>
          </Box>
          <Typography variant="body2" color="textSecondary">
            Review the server-ranked audit queue, scoring inputs, and recent activity.
          </Typography>
        </Box>
        <Box className={classes.controls}>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Scoring location"
            className={classes.locationField}
            value={location.key}
            onChange={event => {
              const nextLocation = LOCATIONS.find(item => item.key === event.target.value)
              if (nextLocation) setLocation(nextLocation)
            }}
          >
            {location.key === 'current-location' && (
              <MenuItem value="current-location">Current Location</MenuItem>
            )}
            {LOCATIONS.map(item => (
              <MenuItem key={item.key} value={item.key}>{item.label}</MenuItem>
            ))}
          </TextField>
          <Tooltip title="Use browser location">
            <Button variant="outlined" startIcon={<MyLocationIcon />} onClick={handleUseCurrentLocation}>
              Use my location
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            color="primary"
            startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />}
            disabled={syncing || refreshing}
            onClick={handleAspireSync}
          >
            Sync Aspire
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            disabled={refreshing}
            onClick={() => loadDashboard({ quiet: true })}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Contracted" value={counts.contracted || 0} helper="Recurring herbicide properties" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Available" value={counts.available || 0} helper="Eligible for queue ranking" color="#1f5e3a" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Deferred" value={counts.deferred || 0} helper="Temporarily held" color="#8c5b00" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Excluded" value={counts.excluded || 0} helper="Removed by an operator" color="#a9341d" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Data review" value={counts.dataQuality || 0} helper="Missing or neutral inputs" color="#3454c1" />
        </Grid>
      </Grid>

      <Grid container spacing={2} className={classes.workspace}>
        <Grid item xs={12} lg={8}>
          <Paper className={classes.queueCard}>
            <Box className={classes.sectionHeader}>
              <Box>
                <Typography variant="h6">Optimized working queue</Typography>
                <Typography variant="caption">
                  Ranked by formula {queue?.formulaVersion || '—'} from {location.label}; optimized order may differ from score rank.
                </Typography>
              </Box>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search queue"
                className={classes.searchField}
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
            </Box>
            <Divider />

            {loading ? (
              <Box className={classes.loading}><CircularProgress /></Box>
            ) : filteredStops.length ? (
              <TableContainer>
                <Table size="small" className={classes.table}>
                  <TableHead className={classes.tableHeader}>
                    <TableRow>
                      <TableCell>Route</TableCell>
                      <TableCell>Property</TableCell>
                      <TableCell align="right">Score</TableCell>
                      <TableCell>Audit age</TableCell>
                      <TableCell align="right">Distance</TableCell>
                      <TableCell align="right">Priority</TableCell>
                      <TableCell>Why it surfaced</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStops.map(stop => (
                      <TableRow
                        key={stop.aspirePropertyId}
                        hover
                        className={`${classes.tableRow} ${selectedPropertyId === stop.aspirePropertyId ? classes.selectedRow : ''}`}
                        onClick={() => setSelectedPropertyId(stop.aspirePropertyId)}
                      >
                        <TableCell><span className={classes.rank}>{stop.optimizedOrder || stop.scoreRank}</span></TableCell>
                        <TableCell>
                          <Typography variant="body2" className={classes.propertyName}>{stop.name}</Typography>
                          <Typography variant="caption">{stop.address || '—'} · Aspire #{stop.aspirePropertyId}</Typography>
                        </TableCell>
                        <TableCell align="right"><strong>{formatScore(stop.queueScore)}</strong></TableCell>
                        <TableCell>{formatAge(stop.ageDays, stop.neverAudited)}</TableCell>
                        <TableCell align="right">{formatDistance(stop.distanceMiles)}</TableCell>
                        <TableCell align="right">{formatPercent(stop.scoreComponents?.businessPriority)}</TableCell>
                        <TableCell>
                          <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                            {(stop.reasons || []).length ? stop.reasons.map(reason => (
                              <Chip key={reason} size="small" label={reason.replace(/_/g, ' ')} />
                            )) : <Typography variant="caption">Weighted score</Typography>}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box className={classes.empty}>
                <AssessmentIcon fontSize="large" />
                <Typography variant="h6">No queue stops found</Typography>
                <Typography variant="body2">Try clearing the search or refreshing from another location.</Typography>
              </Box>
            )}
          </Paper>

          {selectedStop && (
            <Card className={classes.detailCard}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ gap: 12 }}>
                  <Box>
                    <Typography variant="overline">Selected queue stop</Typography>
                    <Typography variant="h6">{selectedStop.name}</Typography>
                    <Typography variant="body2" color="textSecondary">{selectedStop.address || '—'}</Typography>
                  </Box>
                  <Chip
                    color="primary"
                    label={`Score rank #${selectedStop.scoreRank}`}
                    icon={<AssessmentIcon />}
                  />
                </Box>
                <Box className={classes.detailGrid}>
                  <Box className={classes.detailMetric}>
                    <Typography variant="caption">Profitability</Typography>
                    <Typography variant="h6">{formatPercent(selectedStop.scoreComponents?.profitability)}</Typography>
                  </Box>
                  <Box className={classes.detailMetric}>
                    <Typography variant="caption">Contract density</Typography>
                    <Typography variant="h6">{formatPercent(selectedStop.scoreComponents?.density)}</Typography>
                  </Box>
                  <Box className={classes.detailMetric}>
                    <Typography variant="caption">Strategic priority</Typography>
                    <Typography variant="h6">{formatPercent(selectedStop.scoreComponents?.strategic)}</Typography>
                  </Box>
                  <Box className={classes.detailMetric}>
                    <Typography variant="caption">Proximity boost</Typography>
                    <Typography variant="h6">{formatScore(selectedStop.scoreComponents?.proximityBoost)}</Typography>
                  </Box>
                </Box>
                <Box mt={2} display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                  <Chip size="small" icon={<LocationOnIcon />} label={formatDistance(selectedStop.distanceMiles)} />
                  <Chip size="small" icon={<ScheduleIcon />} label={formatAge(selectedStop.ageDays, selectedStop.neverAudited)} />
                  <Chip size="small" label={`Last audit: ${formatDateTime(selectedStop.lastAuditAt)}`} />
                  {(selectedStop.dataQuality || []).map(flag => (
                    <Chip key={flag} size="small" variant="outlined" color="secondary" label={flag.replace(/_/g, ' ')} />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper className={classes.activityCard}>
            <Box className={classes.sectionHeader}>
              <Box className={classes.titleRow}>
                <HistoryIcon color="primary" />
                <Box>
                  <Typography variant="h6">Recent activity</Typography>
                  <Typography variant="caption">Audit submissions and queue decisions</Typography>
                </Box>
              </Box>
              <Tooltip title="Refresh activity">
                <IconButton size="small" onClick={() => loadDashboard({ quiet: true })}>
                  <AutorenewIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Divider />
            <Box className={classes.activityList}>
              {loading ? (
                <Box className={classes.loading}><CircularProgress /></Box>
              ) : activity.length ? activity.map(item => (
                <Box key={item.id} className={classes.activityItem}>
                  <Box display="flex" alignItems="flex-start">
                    <span
                      className={classes.activityIcon}
                      style={{
                        color: item.type === 'audit' ? '#2f7a3e' : '#3454c1',
                        background: item.type === 'audit' ? '#dceedf' : '#e6ebfa'
                      }}
                    >
                      {item.type === 'audit' ? <CheckCircleOutlineIcon fontSize="small" /> : <HistoryIcon fontSize="small" />}
                    </span>
                    <Box minWidth={0}>
                      <Typography variant="body2" className={classes.propertyName}>{item.propertyName}</Typography>
                      <Typography variant="body2">{formatActivityAction(item)}</Typography>
                      {item.reason && <Typography variant="caption" display="block">{item.reason}</Typography>}
                      <Typography variant="caption" color="textSecondary">
                        {formatDateTime(item.occurredAt)}{item.actorName ? ` · ${item.actorName}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )) : (
                <Box className={classes.empty}>
                  <ErrorOutlineIcon fontSize="large" />
                  <Typography variant="h6">No activity yet</Typography>
                  <Typography variant="body2">Completed audits and queue changes will appear here.</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {queue?.warning && (
        <Box mt={2}>
          <Alert severity="warning">{queue.warning}</Alert>
        </Box>
      )}

      <Snackbar
        open={!!notice}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notice ? (
          <Alert severity={notice.severity} variant="filled" onClose={() => setNotice(null)}>
            {notice.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  )
}

const mapStateToProps = state => ({
  url: state.url
})

export default connect(mapStateToProps)(SiteAuditQueueDashboard)
